import { createReducer, on } from '@ngrx/store';
import { ActiveComponentChanged } from 'src/app/shared/actions/eliqua.actions';
import {
  ChangePasswordVisibility,
  IntegrationUserChanged,
  Update,
  UserBusinessGroupChanged,
  UserCountryChanged,
  UserDeactivatedChanged,
  UserDisplayedNameChanged,
  UserEmailChanged,
  UserLanguageChanged,
  UserNameChanged,
  UserPasswdChanged,
  UserQuicksightChanged,
  UserSecGroupChanged,
} from '../actions/edit-user.actions';
import { CloseEditUserDialog, EditUser } from '../actions/security-groups.actions';
import { CloseSidenav, UpdateUser } from '../actions/side-nav.actions';
import {
  GetAvailableLanguagesSuccess,
  GetBusinessGroupsSuccess,
  GetCountriesSuccess,
  GetSecGroupsSuccess,
  GetSecGroupSuccess,
} from '../actions/user-search.actions';
import { EditUserState } from '../models/user.models';

const initialState: EditUserState = {
  userConfiguration: '',
  userName: '',
  userPassword: '',
  passwordVisible: false,
  deactivated: false,
  quicksight: false,
  integrationUser: false,
  displayedName: '',
  email: '',
  securityGroups: '',
  securityGroupValues: [],
  securityGroup: '',
  businessGroups: '',
  businessGroupValues: [],
  businessGroup: '',
  languages: '',
  languageValues: [],
  language: '',
  countriesRaw: '',
  countries: [],
  country: [],
  submit: false,
};

export const EditUserReducer = createReducer(
  initialState,
  on(GetSecGroupsSuccess, (state, { res }) => ({
    ...state,
    securityGroups: res,
    securityGroupValues: res.map((object: any) => object.securityGroupName),
  })),
  on(GetSecGroupSuccess, (state, { res }) => {
    let securityGroupValues: string[] = [...state.securityGroupValues];
    let groupName: string = '';
    state.securityGroups.map((secGroup: any) => {
      if (secGroup.securityGroupId === res.securityGroupId) groupName = secGroup.securityGroupName;
    });
    securityGroupValues.filter((secGroup) => secGroup !== groupName).push(res.securityGroupName);

    return {
      ...state,
      securityGroups: state.securityGroups.map((secGroup: any) =>
        secGroup.securityGroupId === res.securityGroupId ? res : secGroup,
      ),
      securityGroupValues,
    };
  }),
  on(GetBusinessGroupsSuccess, (state, { res }) => ({
    ...state,
    businessGroups: res,
    businessGroupValues: res.map((object: any) => object.businessGroupName),
  })),
  on(GetAvailableLanguagesSuccess, (state, { res }) => ({
    ...state,
    languages: res,
    languageValues: res.map((object: any) => object.languageName),
  })),
  on(GetCountriesSuccess, (state, { res }) => ({
    ...state,
    countriesRaw: res,
    countries: res.map((object: any) => object.country),
  })),
  on(UserNameChanged, (state, { userName }) => ({
    ...state,
    userName,
  })),
  on(UserPasswdChanged, (state, { userPassword }) => ({
    ...state,
    userPassword,
  })),
  on(ChangePasswordVisibility, (state) => ({
    ...state,
    passwordVisible: !state.passwordVisible,
  })),
  on(UserDeactivatedChanged, (state) => ({
    ...state,
    deactivated: !state.deactivated,
  })),
  on(UserQuicksightChanged, (state) => ({
    ...state,
    quicksight: !state.quicksight,
  })),
  on(IntegrationUserChanged, (state) => ({
    ...state,
    integrationUser: !state.integrationUser,
  })),
  on(UserDisplayedNameChanged, (state, { displayedName }) => ({
    ...state,
    displayedName,
  })),
  on(UserEmailChanged, (state, { email }) => ({
    ...state,
    email,
  })),
  on(UserSecGroupChanged, (state, { securityGroup }) => ({
    ...state,
    securityGroup,
  })),
  on(UserBusinessGroupChanged, (state, { businessGroup }) => ({
    ...state,
    businessGroup,
  })),
  on(UserLanguageChanged, (state, { language }) => ({
    ...state,
    language,
  })),
  on(UserCountryChanged, (state, { country }) => ({
    ...state,
    country,
  })),
  on(Update, (state) => ({
    ...state,
    submit: true,
  })),
  on(UpdateUser, (state, { row }) => ({
    ...state,
    userConfiguration: row,
    userName: row.userName,
    deactivated: row.deleteFlag,
    quicksight: row.isQuicksight,
    integrationUser: row.integrationUser,
    displayedName: row.displayedName,
    email: row.emailAddress,
    securityGroup: state.securityGroups
      ? (state.securityGroups.find(
          (secGroup: any) => secGroup.securityGroupId === row.securityGroupId,
        )?.securityGroupName ?? null)
      : null,
    businessGroup: state.businessGroups
      ? (state.businessGroups.find(
          (bsnsGroup: any) => bsnsGroup.businessGroupId === row.businessGroupId,
        )?.businessGroupName ?? null)
      : null,
    language: state.languages
      ? (state.languages.find((lang: any) => lang.languageId === row.appLanguageId)?.languageName ??
        null)
      : null,
    country: state.countriesRaw
      ? row.tableCountryIdList.map(
          (countryId: number) =>
            state.countriesRaw.find((country: any) => country.id === countryId).country ?? null,
        )
      : null,
  })),
  on(EditUser, (state, { user }) => ({
    ...state,
    userConfiguration: user,
    userName: user.userName,
    deactivated: user.deleteFlag,
    quicksight: user.isQuicksight,
    integrationUser: user.integrationUser,
    displayedName: user.displayedName,
    email: user.emailAddress,
    securityGroup: state.securityGroups
      ? (state.securityGroups.find(
          (secGroup: any) => secGroup.securityGroupId === user.securityGroupId,
        )?.securityGroupName ?? null)
      : null,
    businessGroup: state.businessGroups
      ? (state.businessGroups.find(
          (bsnsGroup: any) => bsnsGroup.businessGroupId === user.businessGroupId,
        )?.businessGroupName ?? null)
      : null,
    language: state.languages
      ? (state.languages.find((lang: any) => lang.languageId === user.appLanguageId)
          ?.languageName ?? null)
      : null,
    country: state.countriesRaw
      ? user.tableCountryIdList.map(
          (countryId: number) =>
            state.countriesRaw.find((country: any) => country.id === countryId).country ?? null,
        )
      : null,
  })),
  on(CloseSidenav, CloseEditUserDialog, ActiveComponentChanged, (state) => ({
    ...state,
    userConfiguration: '',
    userName: '',
    userPassword: '',
    deactivated: false,
    quicksight: false,
    integrationUser: false,
    displayedName: '',
    email: '',
    securityGroup: '',
    businessGroup: '',
    language: '',
    country: [],
    submit: false,
  })),
);
